<template>
  <div class="rubik-stage" ref="stage">
    <canvas ref="canvas" class="rubik-canvas" />
    <div class="rubik-labels" ref="labelsRoot" aria-hidden="true" />
    <transition name="rubik-tooltip">
      <div
        v-if="hovered && hovered.title"
        class="rubik-tooltip"
        :style="tooltipStyle"
        role="tooltip"
      >
        <div class="rubik-tooltip-eyebrow">{{ hovered.index }} / 8</div>
        <div class="rubik-tooltip-title">{{ hovered.title }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { translationStore } from "@/stores/translation";

export default {
  name: "AxonHero",
  emits: ["select-service"],
  setup(_, { emit }) {
    const stage = ref(null);
    const canvas = ref(null);
    const labelsRoot = ref(null);
    const hovered = ref(null);
    const tooltipStyle = reactive({ left: "0px", top: "0px" });
    const translation = translationStore();

    let renderer, scene, camera, controls, raf;
    let cubies = [];
    let serviceCubies = []; // subset of cubies that have a service mapping
    let labels = [];        // [{ div, cubie }] HTML overlays for cubie numbers
    let visible = true;     // toggled by IntersectionObserver
    let intersectionObserver = null;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const tmpVec = new THREE.Vector3();
    let lastHoverId = null;
    let pointerInside = false;

    // Service positions in the 3×3×3 grid (each axis ∈ {-1, 0, 1}).
    const SERVICES = [
      { key: "consulting",   titleKey: "AXON_SVC_CONSULTING_TITLE",   pos: [ 1,  1,  1], material: "polished_green" },
      { key: "sovereignty",  titleKey: "AXON_SVC_SOVEREIGNTY_TITLE",  pos: [-1,  1,  1], material: "frosted_green" },
      { key: "cybersec",     titleKey: "AXON_SVC_CYBERSEC_TITLE",     pos: [ 1, -1,  1], material: "shield_glass" },
      { key: "maintenance",  titleKey: "AXON_SVC_MAINTENANCE_TITLE",  pos: [ 1,  0,  0], material: "metallic_deep" },
      { key: "cloud",        titleKey: "AXON_SVC_CLOUD_TITLE",        pos: [ 0,  1,  0], material: "vapor" },
      { key: "agentic",      titleKey: "AXON_SVC_AGENTIC_TITLE",      pos: [-1,  0,  0], material: "clear_glass" },
      { key: "software",     titleKey: "AXON_SVC_SOFTWARE_TITLE",     pos: [ 0, -1,  0], material: "emissive_bright" },
      { key: "model",        titleKey: "AXON_SVC_MODEL_TITLE",        pos: [ 0,  0,  1], material: "crystal_green" },
    ];

    const CUBE_SIZE = 0.6;
    const GAP = 0.06;
    const STEP = CUBE_SIZE + GAP;
    const CUBE_RADIUS = 0.06;

    const COLOR_BLUE_DEEP = new THREE.Color("#0E3A5C");
    const COLOR_BLUE = new THREE.Color("#1E6FB0");
    const COLOR_GREEN_ACCENT = new THREE.Color("#00A651");
    const COLOR_BLUE_BRIGHT = new THREE.Color("#2E89D6");
    const COLOR_BLUE_LIGHT = new THREE.Color("#4BA3E8");

    function makeMaterial(kind) {
      switch (kind) {
        case "polished_green":
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE, metalness: 0.2, roughness: 0.22,
            clearcoat: 0.85, clearcoatRoughness: 0.18,
            emissive: COLOR_BLUE_DEEP, emissiveIntensity: 0.12,
          });
        case "frosted_green":
          return new THREE.MeshPhysicalMaterial({
            color: 0xffffff, metalness: 0.0, roughness: 0.55,
            transmission: 1.0, thickness: 1.0, ior: 1.5,
            attenuationColor: COLOR_BLUE_DEEP, attenuationDistance: 0.6,
            transparent: true, clearcoat: 0.4,
          });
        case "crystal_green":
          return new THREE.MeshPhysicalMaterial({
            color: 0xffffff, metalness: 0.0, roughness: 0.04,
            transmission: 1.0, thickness: 1.6, ior: 1.52,
            attenuationColor: COLOR_BLUE_BRIGHT, attenuationDistance: 1.2,
            transparent: true, clearcoat: 1.0, clearcoatRoughness: 0.05,
            // dispersion off — costs ~3× per fragment. Visual loss is small
            // because the attenuation already gives a green chromatic feel.
          });
        case "metallic_deep":
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE_DEEP, metalness: 0.95, roughness: 0.18,
            clearcoat: 0.5, clearcoatRoughness: 0.25,
            emissive: COLOR_BLUE_DEEP, emissiveIntensity: 0.06,
          });
        case "vapor":
          return new THREE.MeshPhysicalMaterial({
            color: 0xffffff, metalness: 0.0, roughness: 0.65,
            transmission: 0.9, thickness: 0.5, ior: 1.35,
            attenuationColor: COLOR_BLUE_LIGHT, attenuationDistance: 0.9,
            transparent: true, opacity: 0.85,
          });
        case "clear_glass":
          return new THREE.MeshPhysicalMaterial({
            color: 0xffffff, metalness: 0.0, roughness: 0.0,
            transmission: 1.0, thickness: 1.3, ior: 1.5,
            transparent: true, clearcoat: 1.0, clearcoatRoughness: 0.0,
          });
        case "emissive_bright":
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE_BRIGHT, metalness: 0.05, roughness: 0.35,
            emissive: COLOR_BLUE_BRIGHT, emissiveIntensity: 0.55,
            clearcoat: 0.6,
          });
        case "shield_glass":
          // Cybersecurity — single royal-green tactical accent cubie among
          // the icy-blue cube; glassy green glow.
          return new THREE.MeshPhysicalMaterial({
            color: 0xCFE8FB, metalness: 0.1, roughness: 0.32,
            transmission: 0.85, thickness: 0.85, ior: 1.45,
            transparent: true, clearcoat: 0.85, clearcoatRoughness: 0.12,
            attenuationColor: COLOR_GREEN_ACCENT, attenuationDistance: 0.9,
            emissive: COLOR_GREEN_ACCENT, emissiveIntensity: 0.18,
          });

        // ---- background-cubie variations (vary the green) ----
        case "deep_green":
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE_DEEP, metalness: 0.3, roughness: 0.4,
            clearcoat: 0.4, emissive: COLOR_BLUE_DEEP, emissiveIntensity: 0.05,
          });
        case "bright_green":
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE_BRIGHT, metalness: 0.15, roughness: 0.25,
            clearcoat: 0.7, emissive: COLOR_BLUE_BRIGHT, emissiveIntensity: 0.16,
          });
        case "muted_glass":
          // Background-cubie translucent. Plain opacity instead of full
          // transmission — looks ~the same on a small cubie and skips the
          // expensive transmission render pass for these 19 cubies.
          return new THREE.MeshPhysicalMaterial({
            color: 0xDCEEFB, metalness: 0.0, roughness: 0.45,
            transparent: true, opacity: 0.55, clearcoat: 0.3,
            emissive: COLOR_BLUE_LIGHT, emissiveIntensity: 0.04,
          });

        default:
          // Plain cubies: subtle variations of the brand green so the cube
          // doesn't look totally uniform but still reads as one object.
          return new THREE.MeshPhysicalMaterial({
            color: COLOR_BLUE, metalness: 0.18, roughness: 0.32,
            clearcoat: 0.5, clearcoatRoughness: 0.3,
            emissive: COLOR_BLUE_DEEP, emissiveIntensity: 0.08,
          });
      }
    }

    // Deterministic background variant per cubie position so it's not
    // uniform polished_green but still stable across renders.
    function bgMaterialKey(x, y, z) {
      const h = (x + 1) * 9 + (y + 1) * 3 + (z + 1); // 0-26
      const variants = [
        "polished_green", "deep_green", "bright_green",
        "polished_green", "muted_glass", "polished_green",
        "bright_green",   "deep_green", "muted_glass",
      ];
      return variants[h % variants.length];
    }

    function makeEnvTexture() {
      const c = document.createElement("canvas");
      c.width = 1024; c.height = 512;
      const ctx = c.getContext("2d");
      const grad = ctx.createLinearGradient(0, 0, 0, 512);
      grad.addColorStop(0.0, "#FFFFFF");
      grad.addColorStop(0.45, "#F2F9FE");
      grad.addColorStop(0.55, "#DCEEFB");
      grad.addColorStop(1.0, "#9EC8EC");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1024, 512);
      ctx.fillStyle = "rgba(30, 111, 176, 0.12)";
      ctx.fillRect(0, 220, 1024, 12);
      ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
      ctx.fillRect(0, 80, 1024, 40);
      ctx.fillRect(0, 360, 1024, 24);
      const tex = new THREE.CanvasTexture(c);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    }

    function buildRubiksCube() {
      const group = new THREE.Group();
      const sharedGeo = new RoundedBoxGeometry(CUBE_SIZE, CUBE_SIZE, CUBE_SIZE, 4, CUBE_RADIUS);

      // Map service positions for fast lookup.
      const serviceByKey = new Map();
      SERVICES.forEach((s, i) => {
        serviceByKey.set(`${s.pos[0]},${s.pos[1]},${s.pos[2]}`, { svc: s, index: i + 1 });
      });

      // Iterate over the 3×3×3 grid; skip the very center (invisible from outside).
      for (let x = -1; x <= 1; x++) {
        for (let y = -1; y <= 1; y++) {
          for (let z = -1; z <= 1; z++) {
            if (x === 0 && y === 0 && z === 0) continue;

            const key = `${x},${y},${z}`;
            const svcEntry = serviceByKey.get(key);

            const material = svcEntry
              ? makeMaterial(svcEntry.svc.material)
              : makeMaterial(bgMaterialKey(x, y, z));

            const mesh = new THREE.Mesh(sharedGeo, material);
            mesh.position.set(x * STEP, y * STEP, z * STEP);

            const baseEm = material.emissiveIntensity ?? 0;
            mesh.userData = {
              isService: !!svcEntry,
              serviceKey: svcEntry?.svc?.key || null,
              serviceIndex: svcEntry?.index || null,
              titleKey: svcEntry?.svc?.titleKey || null,
              basePos: mesh.position.clone(),
              baseScale: 1,
              targetScale: 1,
              baseEmissive: baseEm,
              targetEmissive: baseEm,
            };

            group.add(mesh);
            cubies.push(mesh);
            if (svcEntry) serviceCubies.push(mesh);
          }
        }
      }

      return group;
    }

    function setupLights(s) {
      s.add(new THREE.AmbientLight(0xffffff, 0.4));
      const key = new THREE.DirectionalLight(0xffffff, 0.95);
      key.position.set(4, 6, 5);
      s.add(key);
      const rim = new THREE.DirectionalLight(0x9ec8ec, 0.55);
      rim.position.set(-5, -2, -3);
      s.add(rim);
      const fill = new THREE.PointLight(0x2E89D6, 0.4, 12);
      fill.position.set(0, 0, 4);
      s.add(fill);
    }

    function onPointerMove(ev) {
      const rect = canvas.value.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      ndc.x = (x / rect.width) * 2 - 1;
      ndc.y = -(y / rect.height) * 2 + 1;
      pointerInside = true;
      tooltipStyle.left = `${x}px`;
      tooltipStyle.top = `${y}px`;
    }

    function onPointerLeave() {
      pointerInside = false;
      ndc.set(10, 10);
    }

    function onClick() {
      if (hovered.value && hovered.value.key) {
        emit("select-service", hovered.value.key);
      }
    }

    function updateHover() {
      raycaster.setFromCamera(ndc, camera);
      const intersects = pointerInside
        ? raycaster.intersectObjects(cubies, false)
        : [];
      const hit = intersects[0]?.object || null;

      cubies.forEach((m) => {
        if (m === hit) {
          m.userData.targetScale = 1.06;
          m.userData.targetEmissive = Math.max(m.userData.baseEmissive + 0.45, 0.55);
        } else {
          m.userData.targetScale = 1;
          m.userData.targetEmissive = m.userData.baseEmissive;
        }
      });

      const id = hit?.userData?.serviceKey || null;
      const isAnyHit = !!hit;
      const newHover = id !== lastHoverId || (isAnyHit && hovered.value === null);
      if (newHover) {
        lastHoverId = id;
        if (hit && hit.userData.isService) {
          hovered.value = {
            key: hit.userData.serviceKey,
            index: hit.userData.serviceIndex,
            title: translation.localize(hit.userData.titleKey),
          };
        } else if (hit) {
          // Non-service cubie — show generic "ping" with no title.
          hovered.value = { key: null, index: 0, title: null };
        } else {
          hovered.value = null;
        }
      }

      if (canvas.value) {
        canvas.value.style.cursor = hit?.userData?.isService ? "pointer" : (hit ? "grab" : "grab");
      }
    }

    function updateLabels() {
      if (!stage.value || labels.length === 0) return;
      const w = stage.value.clientWidth;
      const h = stage.value.clientHeight;
      const camPos = camera.position;
      labels.forEach(({ div, cubie }) => {
        cubie.getWorldPosition(tmpVec);
        // Outward direction of this cubie (its position vector — lattice is
        // centered at origin) tells us which face is "outside."
        const outward = tmpVec.clone().normalize();
        const camDir = camPos.clone().sub(tmpVec).normalize();
        const facing = outward.dot(camDir); // > 0 = facing camera, < 0 = on far side

        // Project to screen.
        tmpVec.project(camera);
        const x = (tmpVec.x * 0.5 + 0.5) * w;
        const y = (-tmpVec.y * 0.5 + 0.5) * h;

        // Smooth fade from facing -0.05 to 0.45 — labels disappear on the back side.
        const opacity = THREE.MathUtils.smoothstep(facing, -0.05, 0.45);
        div.style.opacity = opacity.toFixed(3);
        div.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
      });
    }

    function tick() {
      raf = requestAnimationFrame(tick);
      // Skip ALL work when the cube isn't on screen — orbiting controls,
      // lerps, raycast, label updates, render. Resumes from where it left
      // off when the user scrolls back to it.
      if (!visible) return;

      cubies.forEach((m) => {
        const ud = m.userData;
        m.scale.x += (ud.targetScale - m.scale.x) * 0.18;
        m.scale.y += (ud.targetScale - m.scale.y) * 0.18;
        m.scale.z += (ud.targetScale - m.scale.z) * 0.18;
        if (m.material && m.material.emissiveIntensity !== undefined) {
          m.material.emissiveIntensity +=
            (ud.targetEmissive - m.material.emissiveIntensity) * 0.12;
        }
      });

      controls.update();
      updateHover();
      updateLabels();
      renderer.render(scene, camera);
    }

    function resize() {
      if (!stage.value || !renderer) return;
      const w = stage.value.clientWidth;
      const h = stage.value.clientHeight;
      // Cap DPR at 1.5 — invisible on most displays, ~30-40% fewer pixels to
      // shade than DPR=2.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      renderer.setPixelRatio(dpr);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    async function createRenderer() {
      try {
        const webgpu = await import("three/webgpu");
        if (typeof navigator !== "undefined" && navigator.gpu) {
          const r = new webgpu.WebGPURenderer({
            canvas: canvas.value,
            antialias: true, alpha: true,
            powerPreference: "high-performance",
          });
          await r.init();
          return r;
        }
      } catch (e) { /* fall through */ }
      const r = new THREE.WebGLRenderer({
        canvas: canvas.value, antialias: true, alpha: true,
        powerPreference: "high-performance",
      });
      r.outputColorSpace = THREE.SRGBColorSpace;
      return r;
    }

    onMounted(async () => {
      scene = new THREE.Scene();
      scene.background = null;
      scene.environment = makeEnvTexture();
      scene.environmentIntensity = 0.85;

      camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
      camera.position.set(3.4, 2.6, 4.6);
      camera.lookAt(0, 0, 0);

      renderer = await createRenderer();
      renderer.setClearColor(0x000000, 0);
      if (renderer.toneMapping !== undefined) {
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.05;
      }
      // Halve the transmission render target — biggest single perf win for
      // a scene with many transmission materials. Imperceptible visually.
      if (renderer.transmissionResolutionScale !== undefined) {
        renderer.transmissionResolutionScale = 0.5;
      }

      setupLights(scene);
      scene.add(buildRubiksCube());

      // Create one HTML number label per service cubie (added inside the
      // stage container so they overlay the canvas).
      if (labelsRoot.value) {
        serviceCubies.forEach((cubie) => {
          const div = document.createElement("div");
          div.className = "rubik-label";
          div.textContent = String(cubie.userData.serviceIndex).padStart(2, "0");
          labelsRoot.value.appendChild(div);
          labels.push({ div, cubie });
        });
      }

      controls = new OrbitControls(camera, canvas.value);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enableZoom = false;
      controls.enablePan = false;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.35;
      controls.rotateSpeed = 0.7;

      // Stop auto-rotate as soon as the user grabs the cube.
      const stopAutoRotate = () => { controls.autoRotate = false; };
      controls.addEventListener("start", stopAutoRotate);

      resize();
      window.addEventListener("resize", resize);
      canvas.value.addEventListener("pointermove", onPointerMove);
      canvas.value.addEventListener("pointerleave", onPointerLeave);
      canvas.value.addEventListener("click", onClick);

      // Observe stage visibility — pause rendering when offscreen.
      if (typeof IntersectionObserver !== "undefined" && stage.value) {
        intersectionObserver = new IntersectionObserver(
          (entries) => { visible = entries[0]?.isIntersecting ?? true; },
          { threshold: 0 }
        );
        intersectionObserver.observe(stage.value);
      }

      raf = requestAnimationFrame(tick);
    });

    onBeforeUnmount(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (canvas.value) {
        canvas.value.removeEventListener("pointermove", onPointerMove);
        canvas.value.removeEventListener("pointerleave", onPointerLeave);
        canvas.value.removeEventListener("click", onClick);
      }
      intersectionObserver?.disconnect?.();
      controls?.dispose?.();
      cubies.forEach((m) => {
        m.geometry?.dispose();
        m.material?.dispose();
      });
      labels.forEach(({ div }) => div.remove());
      labels = [];
      renderer?.dispose?.();
    });

    return { stage, canvas, labelsRoot, hovered, tooltipStyle };
  },
};
</script>

<style scoped>
.rubik-stage {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 480px;
}
.rubik-canvas {
  display: block;
  width: 100%;
  height: 100%;
  background: transparent;
  cursor: grab;
}
.rubik-canvas:active { cursor: grabbing; }

.rubik-labels {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.rubik-labels :deep(.rubik-label) {
  position: absolute;
  top: 0;
  left: 0;
  font-family: var(--axon-font-display, 'Manrope', sans-serif);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #FFFFFF;
  background: rgba(10, 37, 64, 0.80);
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 2px 6px rgba(10, 37, 64, 0.30);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  transition: opacity 220ms ease;
}

.rubik-tooltip {
  position: absolute;
  transform: translate(16px, -50%);
  pointer-events: none;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(30, 111, 176, 0.20);
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 12px 32px -12px rgba(14, 58, 92, 0.28);
  min-width: 180px;
  z-index: 4;
}
.rubik-tooltip-eyebrow {
  font-family: var(--axon-font-body, 'Inter', sans-serif);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--axon-blue-600, #1E6FB0);
  margin-bottom: 4px;
}
.rubik-tooltip-title {
  font-family: var(--axon-font-display, 'Manrope', sans-serif);
  font-size: 1rem;
  font-weight: 600;
  color: var(--axon-ink-900, #0A0F0C);
}
.rubik-tooltip-enter-active,
.rubik-tooltip-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}
.rubik-tooltip-enter-from,
.rubik-tooltip-leave-to {
  opacity: 0;
  transform: translate(16px, -50%) scale(0.96);
}
</style>
